/**
 * ルビ記法（｜text《ruby》）をHTMLのrubyタグに変換する
 *
 * @example
 * parseRubyText("｜煉獄の双剣《インフェルノ・デュアルソード》")
 * // => "<ruby>煉<rp>（</rp><rt>インフェ</rt><rp>）</rp></ruby><ruby>獄<rp>（</rp><rt>ルノ・</rt><rp>）</rp></ruby>..."
 */

const RUBY_PATTERN = /｜([^《]+)《([^》]+)》/g;

/**
 * HTMLの特殊文字をエスケープする（XSS対策）
 * @param str - エスケープ対象の文字列
 * @returns エスケープされた文字列
 */
const escapeHtml = (str: string): string =>
  str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

/**
 * ベーステキストとルビテキストを分割して複数のrubyタグを生成する
 * ルビが長い場合でも折り返しが効くようにするため、1文字ずつペアリングする
 *
 * @param baseText - ルビを振る対象のテキスト
 * @param rubyText - ルビテキスト
 * @returns 分割されたrubyタグのHTML文字列
 */
const createSplitRubyTags = (baseText: string, rubyText: string): string => {
  const baseChars = Array.from(baseText);
  const rubyChars = Array.from(rubyText);
  const baseLength = baseChars.length;
  const rubyLength = rubyChars.length;

  // ルビの文字数をベーステキストの文字数で均等に分割
  const charsPerBase = Math.ceil(rubyLength / baseLength);

  return baseChars
    .map((baseChar, index) => {
      const startIndex = index * charsPerBase;
      const endIndex = Math.min(startIndex + charsPerBase, rubyLength);
      const rubyPart = rubyChars.slice(startIndex, endIndex).join("");

      return `<ruby>${escapeHtml(baseChar)}<rp>（</rp><rt>${escapeHtml(rubyPart)}</rt><rp>）</rp></ruby>`;
    })
    .join("");
};

/**
 * ルビ記法をHTMLのrubyタグに変換する
 * @param text - 変換対象のテキスト
 * @returns rubyタグに変換されたHTML文字列
 */
export const parseRubyText = (text: string): string => {
  return text.replace(
    RUBY_PATTERN,
    (_match, baseText: string, rubyText: string) => {
      return createSplitRubyTags(baseText, rubyText);
    },
  );
};
